// Client for the spoken-prompt endpoint (GET /api/audio/prompt/{prompt}), which
// headset mode speaks during a detected pause. A prompt's symbolic situation
// token identifies its audio. The clip is fetched when the current prompt
// changes — not when the pause fires — so speech starts as the pause lands
// instead of waiting on Deepgram, and each prompt's clip is fetched once per
// page.
//
// A prompt is a question asked once: background noise that Deepgram reads as
// repeated speech-then-silence must not make the system repeat itself, so each
// prompt is spoken at most once. resetSession clears that memory at the
// boundaries of a recording. It also drops the cached clips, because the server
// answers each request with a different phrasing of the same prompt — that is
// what keeps a visit from sounding like the last one — while the phrasing stays
// put for the length of a visit.
//
// A pause can land before a prompt's audio has arrived — the prompt does not
// exist until the transcript has been extracted, and Deepgram still has to
// synthesize it — so a prompt asked for while its clip is loading plays as soon
// as the clip lands, unless speech has resumed (cancel) or the head prompt has
// moved on first.

/**
 * URL of the spoken audio for a prompt's symbolic situation token, or null when
 * the prompt carries no situation (and so has no spoken wording).
 */
export function promptSpeechUrl(situation: string | undefined): string | null {
	if (!situation) {
		return null;
	}
	return `/api/audio/prompt/${encodeURIComponent(situation)}`;
}

/** User-facing reason a prompt's audio could not be loaded. */
export function promptSpeechErrorMessage(
	situation: string,
	status: number,
): string {
	if (status === 404) {
		return `No spoken wording for ${situation}`;
	}
	if (status === 503) {
		return "Voice prompts are unavailable: the server has no Deepgram key";
	}
	return `Prompt audio failed (HTTP ${status})`;
}

/**
 * The slice of HTMLAudioElement the speaker drives. A fake stands in for it in
 * tests, where there is no DOM.
 */
export interface PromptAudioElement {
	preload: string;
	src: string;
	currentTime: number;
	play(): Promise<void>;
	pause(): void;
}

export interface PromptSpeaker {
	/**
	 * Point the speaker at the prompt to voice. Fetches and caches its audio and
	 * resolves once that settles; passing the same situation again is a no-op
	 * that resolves immediately.
	 */
	select(situation: string | undefined): Promise<void>;
	/**
	 * Ask for the selected prompt to be voiced. A clip that is still loading
	 * plays as soon as it lands, so a pause that outran the audio still speaks.
	 */
	speak(): void;
	/**
	 * Abandon a prompt asked for but not yet played — the technician started
	 * talking again, so the moment for it has passed.
	 */
	cancel(): void;
	/** Stop playback. */
	stop(): void;
	/**
	 * Forget which prompts have been spoken, and drop the cached clips so the
	 * next visit gets freshly chosen phrasings. Called at the boundaries of a
	 * recording, so a new session asks its questions again.
	 */
	resetSession(): void;
}

export interface PromptSpeakerOptions {
	/** Injected in tests; defaults to the global fetch. */
	fetchImpl?: typeof fetch;
	/** Injected in tests; defaults to `new Audio()`. */
	createAudio?: () => PromptAudioElement;
	/** Injected in tests; defaults to URL.createObjectURL. */
	createObjectUrl?: (blob: Blob) => string;
	/** Injected in tests; defaults to URL.revokeObjectURL. */
	revokeObjectUrl?: (url: string) => void;
	/** Receives a user-facing message whenever a prompt cannot be voiced. */
	onError?: (message: string) => void;
}

/**
 * Create the headset-mode prompt speaker. A prompt is fetched by its symbolic
 * situation token and cached by token, so the pause that triggers speech plays a
 * clip that is already in memory. A fetch that a newer selection superseded is
 * discarded rather than overwriting the current clip.
 */
export function createPromptSpeaker(
	options: PromptSpeakerOptions = {},
): PromptSpeaker {
	const fetchImpl = options.fetchImpl ?? ((input) => fetch(input));
	const createAudio = options.createAudio ?? (() => new Audio());
	const createObjectUrl =
		options.createObjectUrl ?? ((blob) => URL.createObjectURL(blob));
	const revokeObjectUrl =
		options.revokeObjectUrl ?? ((url) => URL.revokeObjectURL(url));

	let audio: PromptAudioElement | null = null;
	let selected: string | null = null;
	// The situation whose prompt has been asked for but not yet heard, because
	// its clip was still arriving. Cleared by cancel, by a new head prompt, and
	// once the clip plays.
	let requested: string | null = null;
	// Situation token -> object URL, so each prompt is fetched once per page.
	const loaded = new Map<string, string>();
	// Situation tokens already spoken this session, so noise and silence cannot
	// make the same prompt play over and over.
	const spoken = new Set<string>();
	// Monotonic selection id: a fetch whose id is stale must not touch the
	// element or the cache the current prompt points at.
	let generation = 0;

	function element(): PromptAudioElement {
		if (!audio) {
			audio = createAudio();
			audio.preload = "auto";
		}
		return audio;
	}

	/** Voice a loaded prompt, and remember that this session has asked it. */
	function playPrompt(situation: string): void {
		spoken.add(situation);
		requested = null;
		const el = element();
		el.currentTime = 0;
		void el.play().catch(() => {
			options.onError?.("Prompt audio could not play");
		});
	}

	async function fetchClip(
		situation: string,
		url: string,
		id: number,
	): Promise<void> {
		let response: Response;
		try {
			response = await fetchImpl(url);
		} catch {
			if (id === generation) {
				options.onError?.("Prompt audio request failed");
			}
			return;
		}
		if (!response.ok) {
			if (id === generation) {
				options.onError?.(promptSpeechErrorMessage(situation, response.status));
			}
			return;
		}
		let blob: Blob;
		try {
			blob = await response.blob();
		} catch {
			if (id === generation) {
				options.onError?.("Prompt audio request failed");
			}
			return;
		}
		if (id !== generation) {
			return;
		}
		const objectUrl = createObjectUrl(blob);
		loaded.set(situation, objectUrl);
		element().src = objectUrl;
		if (requested === situation && selected === situation) {
			// The pause outran the audio; speak now, while the moment still holds.
			playPrompt(situation);
		}
	}

	return {
		select(situation) {
			const next = situation ?? null;
			if (next === selected) {
				return Promise.resolve();
			}
			selected = next;
			// A new head prompt abandons what the old one had asked for.
			requested = null;
			generation += 1;
			if (!next) {
				return Promise.resolve();
			}
			const cached = loaded.get(next);
			if (cached) {
				element().src = cached;
				return Promise.resolve();
			}
			const url = promptSpeechUrl(next);
			return url ? fetchClip(next, url, generation) : Promise.resolve();
		},
		speak() {
			if (!selected || spoken.has(selected)) {
				return; // no prompt, or already asked this session
			}
			if (!loaded.has(selected)) {
				// The clip is still arriving: play it the moment it lands.
				requested = selected;
				return;
			}
			playPrompt(selected);
		},
		cancel() {
			requested = null;
		},
		stop() {
			audio?.pause();
			requested = null;
		},
		resetSession() {
			spoken.clear();
			requested = null;
			// Drop the clips too: the next selection re-requests, so the server
			// picks a fresh phrasing for the new visit.
			for (const url of loaded.values()) {
				revokeObjectUrl(url);
			}
			loaded.clear();
		},
	};
}
